import React, { Component, ReactNode, ErrorInfo } from "react";
import { Link } from "react-router-dom";
import { Button } from "./Button";
import { AlertTriangle, Home, RefreshCw } from "lucide-react";
import styles from "./ErrorBoundary.module.css";

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Log the error and component stack to the console
    // This is vital for debugging white-screens in Capacitor WebViews
    console.error("Uncaught error:", error, errorInfo);
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className={styles.container}>
          <div className={styles.card}>
            <div className={styles.iconContainer}>
              <AlertTriangle className={styles.icon} size={48} />
            </div>
            
            <h1 className={styles.title}>Something went wrong</h1>
            
            <p className={styles.description}>
              We encountered an unexpected error. Please try again or return to the home screen.
            </p>
            
            {this.state.error && (
              <div className={styles.errorBlock}>
                <code>{this.state.error.message}</code>
              </div>
            )}
            
            <div className={styles.actions}>
              <Button onClick={this.handleReset} size="lg" className={styles.actionButton}>
                <RefreshCw size={16} /> Try again
              </Button>
              <Button variant="outline" asChild size="lg" className={styles.actionButton}>
                <Link to="/">
                  <Home size={16} /> Home
                </Link>
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}